import { pushLedger } from '../meta/ledger';
import type { MatchOutcome, Profile } from '../meta/types';
import { CHARMS, TWIG_REGROW_MS, TWIG_SKIP_ACORNS, WELCOME_CHARM_PACK, charmById } from './catalog';

export interface CharmStack {
  count: number;
  regrowAt: number | null;
}

export function emptyCharms(): Record<string, CharmStack> {
  const charms: Record<string, CharmStack> = {};
  for (const charm of CHARMS) {
    if (charm.tier === 'twig') charms[charm.id] = { count: 1, regrowAt: null };
  }
  return charms;
}

export function ensureCharms(profile: Profile): void {
  if (!profile.charms) profile.charms = {};
  for (const charm of CHARMS) {
    if (charm.tier !== 'twig') continue;
    const row = profile.charms[charm.id];
    if (!row) profile.charms[charm.id] = { count: 1, regrowAt: null };
  }
}

/** A Twig whose wait has finished is back in the bag. */
export function refreshRegrow(profile: Profile, now: number): void {
  ensureCharms(profile);
  for (const charm of CHARMS) {
    if (charm.tier !== 'twig') continue;
    const row = profile.charms[charm.id];
    if (!row || row.regrowAt === null) continue;
    if (now >= row.regrowAt) {
      row.count = 1;
      row.regrowAt = null;
    }
  }
}

export function charmUnlocked(profile: Profile, id: string): boolean {
  const charm = charmById(id);
  if (!charm) return false;
  if (charm.tier === 'twig') return true;
  return profile.bestTrophies >= charm.unlockAt;
}

export function charmReady(profile: Profile, id: string, now: number): boolean {
  refreshRegrow(profile, now);
  const charm = charmById(id);
  const row = profile.charms[id];
  if (!charm || !row) return false;
  if (!charmUnlocked(profile, id) && row.count <= 0) return false;
  if (charm.tier === 'twig') return row.regrowAt === null && row.count > 0;
  return row.count > 0;
}

/**
 * Keep `preferred` when it can be staked. Otherwise the first ready Charm in
 * catalog order, or none. A lost Twig must not stay selected across a rematch.
 */
export function resolveCharmPick(profile: Profile, preferred: string | null, now: number): string | null {
  refreshRegrow(profile, now);
  if (preferred && charmReady(profile, preferred, now)) return preferred;
  for (const charm of CHARMS) {
    if (charmReady(profile, charm.id, now)) return charm.id;
  }
  return null;
}

/** Why Fight cannot start. Null means the pick can be staked. */
export function charmFightBlock(profile: Profile, id: string | null, now: number): string | null {
  refreshRegrow(profile, now);
  if (!id) return 'Pick a Charm first.';
  if (charmReady(profile, id, now)) return null;
  const charm = charmById(id);
  if (!charm || !charmUnlocked(profile, id)) return 'That Charm is still locked.';
  if (charm.tier === 'twig') return `${charm.name} is still regrowing.`;
  return `${charm.name} is spent.`;
}

export function charmCount(profile: Profile, id: string, now: number): number {
  refreshRegrow(profile, now);
  return profile.charms[id]?.count ?? 0;
}

/**
 * Win and tie keep the Charm. A loss spends one copy.
 * A Twig starts regrowing instead of leaving the catalog.
 */
export function consumeStake(
  profile: Profile,
  id: string,
  outcome: MatchOutcome,
  now: number,
  safeId?: string | null,
): boolean {
  refreshRegrow(profile, now);
  if (outcome !== 'loss') return true;
  if (safeId && id === safeId) return true;
  const charm = charmById(id);
  const row = profile.charms[id];
  if (!charm || !row || row.count <= 0) return false;
  if (charm.tier === 'twig') {
    row.count = 0;
    row.regrowAt = now + TWIG_REGROW_MS;
  } else {
    row.count -= 1;
  }
  pushLedger(profile, now, 'stake', {}, 0, id);
  return false;
}

export function grantCharm(profile: Profile, id: string, count: number): void {
  ensureCharms(profile);
  const charm = charmById(id);
  if (!charm || count <= 0) return;
  const row = profile.charms[id] ?? { count: 0, regrowAt: null };
  if (charm.tier === 'twig') {
    row.count = 1;
    row.regrowAt = null;
  } else {
    row.count += count;
  }
  profile.charms[id] = row;
}

export function grantWelcomePack(profile: Profile): void {
  ensureCharms(profile);
  for (const [id, count] of Object.entries(WELCOME_CHARM_PACK)) grantCharm(profile, id, count);
}

export type BuyFail = 'missing' | 'locked' | 'acorns' | 'honey' | 'free';

export function buyCharm(
  profile: Profile,
  id: string,
  currency: 'acorns' | 'honey',
  now: number,
): { ok: boolean; reason?: BuyFail } {
  const charm = charmById(id);
  if (!charm) return { ok: false, reason: 'missing' };
  if (charm.tier === 'twig') return { ok: false, reason: 'free' };
  if (!charmUnlocked(profile, id)) return { ok: false, reason: 'locked' };
  const price = currency === 'acorns' ? charmPriceAcorns(charm.tier) : charmPriceHoney(charm.tier);
  if (price === null) return { ok: false, reason: currency };
  if (profile.wallet[currency] < price) return { ok: false, reason: currency };
  profile.wallet[currency] -= price;
  grantCharm(profile, id, 1);
  pushLedger(profile, now, 'charm', { [currency]: -price }, 0, id);
  return { ok: true };
}

function charmPriceAcorns(tier: CharmDefTier): number | null {
  if (tier === 'twig') return 0;
  if (tier === 'copper') return 120;
  if (tier === 'silver') return 250;
  if (tier === 'gold') return 500;
  if (tier === 'heartwood') return 1200;
  return null;
}

function charmPriceHoney(tier: CharmDefTier): number | null {
  if (tier === 'twig') return null;
  if (tier === 'copper') return 15;
  if (tier === 'silver') return 30;
  if (tier === 'gold') return 60;
  if (tier === 'heartwood') return 140;
  if (tier === 'starlit') return 400;
  return null;
}

type CharmDefTier = (typeof CHARMS)[number]['tier'];

export function skipTwigRegrow(profile: Profile, id: string, now: number): { ok: boolean; reason?: BuyFail } {
  refreshRegrow(profile, now);
  const charm = charmById(id);
  const row = profile.charms[id];
  if (!charm || charm.tier !== 'twig' || !row || row.regrowAt === null) return { ok: false, reason: 'free' };
  if (profile.wallet.acorns < TWIG_SKIP_ACORNS) return { ok: false, reason: 'acorns' };
  profile.wallet.acorns -= TWIG_SKIP_ACORNS;
  row.count = 1;
  row.regrowAt = null;
  pushLedger(profile, now, 'charm', { acorns: -TWIG_SKIP_ACORNS }, 0, `${id}:regrow`);
  return { ok: true };
}

export { TWIG_REGROW_MS, TWIG_SKIP_ACORNS };
